import React, { useState, useEffect } from 'react';
import { AlertTriangle, Crown, Clock, X, Zap, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';

import { BACKEND_URL } from '../config/api';

/**
 * MissedJobsAlert Component
 * 
 * Shows a banner alert when user has missed urgent jobs.
 * "You missed 2 urgent jobs this week - Priority Boost trades saw this first"
 */
export function MissedJobsAlert({ onUpgrade }) {
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMissedJobsSummary();
  }, []);

  const fetchMissedJobsSummary = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/urgent-notifications/missed-jobs/summary`,
        { withCredentials: true }
      );
      setSummary(response.data);
    } catch (error) {
      console.warn('MissedJobsAlert: failed to fetch summary', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading || dismissed || !summary?.show_alert) {
    return null;
  }

  return (
    <div 
      className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4 relative"
      data-testid="missed-jobs-alert"
    >
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 p-1 text-amber-400 hover:text-amber-600 rounded"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
        </div>
        
        <div className="flex-1">
          <p className="font-semibold text-amber-900 text-sm">
            {summary.message}
          </p>
          <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
            <Crown className="h-3 w-3" />
            {summary.cta}
          </p>
          
          <button
            onClick={onUpgrade}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-lg transition-colors"
            data-testid="missed-jobs-upgrade-btn"
          >
            <Zap className="h-3 w-3" />
            Get instant alerts
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * UrgentJobPaywall Component
 * 
 * Shows when a free user taps on an urgent job.
 * Conversion trigger with "Get urgent jobs first" messaging.
 */
export function UrgentJobPaywall({ open, onOpenChange, onUpgrade, jobDetails = null }) {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = () => {
    setLoading(true);
    onUpgrade?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
            <Clock className="h-6 w-6 text-amber-500" />
            Get urgent jobs first
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            On Call is free for all tradespeople. Refer other trades to earn a Priority Boost and appear higher in employer searches.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Job preview (if available) */}
          {jobDetails && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm font-medium text-slate-900">
                {jobDetails.trade} • {jobDetails.location}
              </p>
              {jobDetails.budget_range && (
                <p className="text-xs text-slate-600 mt-1">
                  Budget: {jobDetails.budget_range}
                </p>
              )}
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <Crown className="h-3 w-3" />
                Pro users saw this {jobDetails.delay_minutes || 10} minutes ago
              </p>
            </div>
          )}

          {/* Benefits */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="h-3 w-3 text-emerald-600" />
              </div>
              <span>Instant notifications for urgent jobs</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="h-3 w-3 text-emerald-600" />
              </div>
              <span>First access before free users</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="h-3 w-3 text-emerald-600" />
              </div>
              <span>Higher chance of getting hired</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="h-3 w-3 text-emerald-600" />
              </div>
              <span>Charge higher rates for short notice work</span>
            </div>
          </div>

          {/* Urgency message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <p className="font-medium">Urgent jobs fill fast</p>
            <p className="text-xs mt-1">
              Most urgent jobs are filled within 10-15 minutes. Don't miss out.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Maybe later
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            data-testid="urgent-job-upgrade-btn"
          >
            <Crown className="h-4 w-4 mr-2" />
            Earn Priority Boost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * MissedJobsList Component
 * 
 * Shows detailed list of missed jobs with upgrade CTA
 */
export function MissedJobsList({ onUpgrade }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMissedJobs();
  }, []);

  const fetchMissedJobs = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/urgent-notifications/missed-jobs`,
        { withCredentials: true }
      );
      setData(response.data);
    } catch (error) {
      console.warn('UrgentJobAlerts: failed to fetch missed jobs', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-16 bg-slate-100 rounded-lg"></div>
        <div className="h-16 bg-slate-100 rounded-lg"></div>
      </div>
    );
  }

  if (!data || data.total_count === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Clock className="h-8 w-8 mx-auto mb-2 text-slate-400" />
        <p className="text-sm">No missed jobs in the last {data?.days_window || 7} days</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="missed-jobs-list">
      {/* Summary */}
      {data.show_upgrade_prompt && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-amber-900">
                {data.this_week_count} missed job{data.this_week_count !== 1 ? 's' : ''} this week
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Priority Boost trades saw these first
              </p>
            </div>
            <Button
              size="sm"
              onClick={onUpgrade}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Crown className="h-4 w-4 mr-1" />
              Upgrade
            </Button>
          </div>
        </div>
      )}

      {/* Job list */}
      <div className="space-y-2">
        {data.missed_jobs.map((mj, index) => (
          <div
            key={mj.missed_job_id || `mj-${index}`}
            className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between"
          >
            <div>
              <p className="text-sm font-medium text-slate-900">
                {mj.job_details?.trade || 'Job'} • {mj.job_details?.location || 'Local area'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {new Date(mj.created_at).toLocaleDateString()} • {mj.reason === 'delayed' ? 'Saw 10 mins late' : 'Not available'}
              </p>
            </div>
            {mj.job_details?.status === 'active' ? (
              <span className="text-xs text-emerald-600 font-medium">Still open</span>
            ) : (
              <span className="text-xs text-slate-400">Filled</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default {
  MissedJobsAlert,
  UrgentJobPaywall,
  MissedJobsList
};
